import React, { useEffect, useMemo, useState } from "react";
import { Form } from "react-bootstrap";
import { useLocation, useNavigate } from "react-router-dom";
import {
  FaArrowLeft,
  FaCalendarAlt,
  FaCertificate,
  FaChild,
  FaClock,
  FaEnvelope,
  FaMapMarkerAlt,
  FaMoneyBillWave,
  FaPhone,
  FaStar,
  FaUtensils,
  FaUser,
  FaUsers,
} from "react-icons/fa";
import NavBar from "./nav";
import Footer from "./footer";
import VisitorTips from "./VisitorTips";
import "../css/resturantDescription.css";
import { notifyError, notifySuccess } from "../utils/notify";

const RestaurantDescription = () => {
  const navigate = useNavigate();
  const { state = {} } = useLocation();

  const {
    title = "Restaurant",
    description = "No description available.",
    location: restaurantLocation = "Location not specified",
    rating = 0,
    priceRange = "R150 and under",
    hours = "08:00 till 21:00",
    cuisineType = "Not specified",
    contactPhone = "",
    contactEmail = "",
    phone = "",
    email = "",
  } = state;

  const gallery = useMemo(() => {
    const images = Array.isArray(state.gallery) ? state.gallery.filter(Boolean) : [];
    return [...new Set([state.image, ...images].filter(Boolean))];
  }, [state.gallery, state.image]);

  const [selectedImage, setSelectedImage] = useState("");
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    date: "",
    time: "",
    guests: 2,
    children: 0,
    comments: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [bookingStatus, setBookingStatus] = useState(null);

  const safeRating = Math.min(5, Math.max(0, Number(rating) || 0));
  const displayPhone = contactPhone || phone;
  const displayEmail = contactEmail || email;

  useEffect(() => {
    setSelectedImage(gallery[0] || "");
  }, [gallery]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setBookingStatus(null);
    setFormData((current) => ({
      ...current,
      [name]: value,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);
    setBookingStatus(null);

    try {
      const response = await fetch(
        "https://kim-tour-app-backend.onrender.com/api/restaurant/reserve",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            ...formData,
            restaurant: title,
            businessEmail: displayEmail || "edward.matiwane@gmail.com",
          }),
        }
      );

      const data = await response.json();

      if (data.success) {
        const message = `Reservation request sent${
          data.bookingId ? `. Reference: ${data.bookingId}` : "."
        }`;
        setBookingStatus({
          success: true,
          message,
        });
        notifySuccess(message);
        setFormData({
          name: "",
          email: "",
          phone: "",
          date: "",
          time: "",
          guests: 2,
          children: 0,
          comments: "",
        });
      } else {
        const message = data.message || "Reservation failed. Please try again.";
        setBookingStatus({
          success: false,
          message,
        });
        notifyError(message);
      }
    } catch (error) {
      const message = "We could not send the reservation. Please try again.";
      setBookingStatus({
        success: false,
        message,
      });
      notifyError(message);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!state.title) {
    return (
      <div className="restaurant-description-page">
        <NavBar />
        <main className="restaurant-detail-container">
          <button className="restaurant-back-button" onClick={() => navigate(-1)} type="button">
            <FaArrowLeft />
            Back
          </button>
          <section className="restaurant-empty-state">
            <h1>Restaurant details unavailable</h1>
            <p>Please return to the restaurant list and choose a place to dine.</p>
          </section>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="restaurant-description-page">
      <NavBar />
      <main className="restaurant-detail-container">
        <header className="restaurant-title-row">
          <button
            className="restaurant-back-button icon-only"
            onClick={() => navigate(-1)}
            type="button"
          >
            <FaArrowLeft />
          </button>
          <div>
            <p className="restaurant-eyebrow">
              <FaUtensils />
              {cuisineType} Cuisine
            </p>
            <h1>{title}</h1>
            <div className="restaurant-meta-row">
              <span>
                <FaMapMarkerAlt />
                {restaurantLocation}
              </span>
              <span>
                <FaStar />
                {safeRating}/5 dining experience
              </span>
            </div>
          </div>
        </header>

        <section className="restaurant-hero-grid">
          <div className="restaurant-main-image">
            {selectedImage ? (
              <img src={selectedImage} alt={title} />
            ) : (
              <div className="restaurant-image-placeholder">
                <FaUtensils />
                <span>{cuisineType} Cuisine</span>
              </div>
            )}
            <div className="restaurant-hero-overlay">
              <span>{priceRange}</span>
              <strong>{hours}</strong>
            </div>
          </div>

          <div className="restaurant-thumbnail-grid">
            {gallery.slice(0, 4).map((src, index) => (
              <button
                key={src}
                className={`restaurant-thumbnail ${
                  selectedImage === src ? "active" : ""
                }`}
                type="button"
                onClick={() => setSelectedImage(src)}
              >
                <img src={src} alt={`${title} view ${index + 1}`} />
              </button>
            ))}
            {!gallery.length && (
              <div className="restaurant-thumbnail empty">
                <FaUtensils />
              </div>
            )}
          </div>
        </section>

        <section className="restaurant-content-grid">
          <div className="restaurant-main-content">
            <div className="restaurant-section restaurant-review-panel">
              <span className="restaurant-icon-badge primary">
                <FaUtensils />
              </span>
              <div>
                <p>Dining Experience</p>
                <div className="restaurant-stars">
                  <strong>{safeRating}</strong>
                  {Array.from({ length: 5 }, (_, index) => (
                    <FaStar
                      key={index}
                      className={index < safeRating ? "filled" : ""}
                    />
                  ))}
                </div>
              </div>
            </div>

            <div className="restaurant-section">
              <h2>About This Restaurant</h2>
              <p>{description}</p>
            </div>

            <div className="restaurant-section">
              <h2>Restaurant Information</h2>
              <div className="restaurant-info-grid">
                <div className="restaurant-info-item cyan">
                  <span>
                    <FaMapMarkerAlt />
                  </span>
                  <div>
                    <strong>Location</strong>
                    <p>{restaurantLocation}</p>
                  </div>
                </div>
                <div className="restaurant-info-item green">
                  <span>
                    <FaMoneyBillWave />
                  </span>
                  <div>
                    <strong>Price Range</strong>
                    <p>{priceRange}</p>
                  </div>
                </div>
                <div className="restaurant-info-item blue">
                  <span>
                    <FaClock />
                  </span>
                  <div>
                    <strong>Opening Hours</strong>
                    <p>{hours}</p>
                  </div>
                </div>
                <div className="restaurant-info-item rose">
                  <span>
                    <FaCertificate />
                  </span>
                  <div>
                    <strong>Food Safety</strong>
                    <p>Fresh ingredients handled with care.</p>
                  </div>
                </div>
              </div>
            </div>

            {(displayPhone || displayEmail) && (
              <div className="restaurant-section">
                <h2>Contact</h2>
                <div className="restaurant-contact-row">
                  {displayPhone && (
                    <span>
                      <FaPhone />
                      {displayPhone}
                    </span>
                  )}
                  {displayEmail && (
                    <span>
                      <FaEnvelope />
                      {displayEmail}
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>

          <aside className="restaurant-reservation-panel">
            <div className="restaurant-reservation-card">
              <span className="restaurant-reservation-icon">
                <FaCalendarAlt />
              </span>
              <h2>Make A Reservation</h2>
              <p>Choose your date, time, and party size. The restaurant will confirm availability.</p>

              {bookingStatus && (
                <div
                  className={`restaurant-booking-status ${
                    bookingStatus.success ? "success" : "error"
                  }`}
                >
                  {bookingStatus.message}
                </div>
              )}

              <Form onSubmit={handleSubmit} className="restaurant-form">
                <Form.Group>
                  <Form.Label>Full Name</Form.Label>
                  <Form.Control
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                  />
                </Form.Group>

                <div className="restaurant-form-grid">
                  <Form.Group>
                    <Form.Label>Email</Form.Label>
                    <Form.Control
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Phone</Form.Label>
                    <Form.Control
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      required
                    />
                  </Form.Group>
                </div>

                <div className="restaurant-form-grid">
                  <Form.Group>
                    <Form.Label>Date</Form.Label>
                    <Form.Control
                      type="date"
                      name="date"
                      value={formData.date}
                      onChange={handleChange}
                      min={new Date().toISOString().split("T")[0]}
                      required
                    />
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>Time</Form.Label>
                    <Form.Control
                      type="time"
                      name="time"
                      value={formData.time}
                      onChange={handleChange}
                      required
                    />
                  </Form.Group>
                </div>

                <div className="restaurant-guest-grid">
                  <Form.Group>
                    <Form.Label>
                      <FaUser />
                      Adults
                    </Form.Label>
                    <Form.Select
                      name="guests"
                      value={formData.guests}
                      onChange={handleChange}
                      required
                    >
                      {[1, 2, 3, 4, 5, 6, 7, 8].map((num) => (
                        <option key={num} value={num}>
                          {num} {num === 1 ? "person" : "people"}
                        </option>
                      ))}
                      <option value="9">9+ people</option>
                    </Form.Select>
                  </Form.Group>
                  <Form.Group>
                    <Form.Label>
                      <FaChild />
                      Children
                    </Form.Label>
                    <Form.Select
                      name="children"
                      value={formData.children}
                      onChange={handleChange}
                    >
                      {[0, 1, 2, 3, 4, 5].map((num) => (
                        <option key={num} value={num}>
                          {num} {num === 1 ? "child" : "children"}
                        </option>
                      ))}
                    </Form.Select>
                  </Form.Group>
                </div>

                <Form.Group>
                  <Form.Label>
                    <FaUsers />
                    Special Requests
                  </Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={4}
                    name="comments"
                    value={formData.comments}
                    onChange={handleChange}
                    placeholder="Dietary restrictions, allergies, seating preferences..."
                  />
                </Form.Group>

                <button className="restaurant-reserve-button" type="submit" disabled={isSubmitting}>
                  {isSubmitting ? "Sending Reservation..." : "Reserve Now"}
                </button>
              </Form>
            </div>
          </aside>
        </section>
      </main>
      <section className="detail-extra-section">
        <VisitorTips title={title} category="Restaurant" />
      </section>
      <Footer />
    </div>
  );
};

export default RestaurantDescription;
